import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadPrintImagesPreference from "@waytek/Handlers/PrintPageUserPreference/LoadPrintImagesUserPreference";
import setPrintImagesPreference from "@waytek/Handlers/PrintPageUserPreference/SetPrintImagesUserPreference";
import { CustomState } from "@waytek/Store/Reducers";
import simplePrintDialog from "@waytek/Utilities/global/simplePrintDialog";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
    logoImage = "logoImage",
}

// Verndale Customization: WYTKXOP-2495
const mapStateToProps = (state: CustomState) => ({
    hideImagesOnPrint: state.printImages.hideImagesOnPrint || false,
    userId: getSession(state).userProfileId || "",
});

const mapDispatchToProps = {
    loadPrintImagesPreference,
    setPrintImagesPreference,
};

// End Verndale Customization

interface OwnProps extends WidgetProps {
    fields: {
        [fields.generateEmailAttachmentFromWebpage]: boolean;
        [fields.logoImage]: string;
    };
}

// Verndale Customization: WYTKXOP-2495
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
// End Verndale Customization

export interface VerndaleStyles {
    allowPrintImageOnPdfCheckbox?: InjectableCss;
    allowPrintImageGridItem?: GridItemProps;
}

export interface InvoiceDetailHeaderStyles extends VerndaleStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
    logoGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    logo?: ImgProps;
    belowLogoTxt?: TypographyProps;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = headerStyles;
const InvoiceDetailHeader: FC<Props> = ({
    fields,
    userId,
    hideImagesOnPrint,
    setPrintImagesPreference,
    loadPrintImagesPreference,
}) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;

    // Verndale Customization: WYTKXOP-2495
    const [allowPrint, setAllowPrint] = useState(hideImagesOnPrint);

    useEffect(() => {
        if (userId && !hideImagesOnPrint) {
            loadPrintImagesPreference({ id: userId });
        }
    }, []);

    useEffect(() => {
        setAllowPrint(hideImagesOnPrint);
    }, [hideImagesOnPrint]);
    // End Verndale Customization

    if (!invoice) {
        return null;
    }

    // Verndale Customization: WYTKXOP-2495
    const handleHideImagesChange = (_: React.SyntheticEvent<Element, Event>) => {
        setAllowPrint(!allowPrint);
        setPrintImagesPreference({ id: userId, hideImagesOnPrint: !hideImagesOnPrint });
    };
    // End Verndale Customization

    const printLabel = translate("Print");

    return (
        <GridContainer {...styles.headerGridContainer}>
            {fields.logoImage && (
                <GridItem {...styles.logoGridItem}>
                    <StyledWrapper {...styles.logoWrapper}>
                        <Img src={fields.logoImage} {...styles.logo} />
                        <Typography {...styles.belowLogoTxt}>
                            {translate("PO Box 1727 Chaska, MN 55318 ph: (800) 328-2724")}
                        </Typography>
                    </StyledWrapper>
                </GridItem>
            )}
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{`${translate("Invoice")} ${invoice.invoiceNumber}`}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                        <ShareEntityButton
                            entityId={invoice.invoiceNumber}
                            variant="clickable"
                            entityName="Invoice"
                            extendedStyles={styles.shareEntityButtonStyles}
                            generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                        />
                        <Clickable
                            {...styles.printClickable}
                            onClick={simplePrintDialog}
                            data-test-selector="invoiceDetails_print"
                        >
                            {printLabel}
                        </Clickable>
                    </OverflowMenu>
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    <ShareEntityButton
                        entityId={invoice.invoiceNumber}
                        entityName="Invoice"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                    />
                    <Button
                        {...styles.printButton}
                        onClick={simplePrintDialog}
                        data-test-selector="invoiceDetails_print"
                    >
                        {printLabel}
                    </Button>
                </Hidden>
            </GridItem>
            {/* Verndale Customization */}
            <GridItem {...styles.allowPrintImageGridItem}>
                <Checkbox
                    {...styles.allowPrintImageOnPdfCheckbox}
                    checked={allowPrint}
                    onChange={e => handleHideImagesChange(e)}
                >
                    {translate("Hide images when printing")}
                </Checkbox>
            </GridItem>
            {/* End Verndale Customization */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailHeader),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
